import type { AccountPreapproval } from "@/lib/brokerAccounts";
import type { BondSubmission } from "@/lib/brokerBonds";
import {
  bidResultFact,
  changeRequestFact,
  readBondActions,
  readBondLifecycle,
  readCapacityCheck,
  readHeaderStatus,
  type BondAction,
  type BondActionKey,
  type BondHeaderStatus,
} from "@/lib/bondStateModel";
import { readGiaStatus } from "@/lib/brokerGia";
import { formatPreapprovalMoney } from "@/lib/bondValueDisplay";
import { OVER_SENTENCE, RELEASED_PHASES } from "@/lib/submissionProfile";

// BRK-S399 — the submission page's v2 shell, as pure reads of the order view:
// the eight tabs, the header pills, the button row's chips and the fixed
// action row.
//
// NOTHING HERE DECIDES ELIGIBILITY (CLAUDE.md #7). The action row and the Bid
// Result button RENDER the server's `actions{}` block (PMS-S837, read by
// `readBondActions`). Every order view carries it (BRK-S500), so there is no
// older rule to fall back to (BRK-S502): a block that is not the published
// shape greys every action, never enables one.

// ── The tabs ─────────────────────────────────────────────────────────────────

/** The eight tabs, in the wireframe's order; held in `?tab=`. */
export const SUBMISSION_TABS = [
  { id: "profile", label: "Submission Profile" },
  { id: "program", label: "Surety Program" },
  { id: "details", label: "Project Details" },
  { id: "bonds", label: "Bonds" },
  { id: "documents", label: "Submission Documents" },
  { id: "messages", label: "Messages" },
  { id: "billing", label: "Billing" },
  { id: "history", label: "History" },
] as const;

export type SubmissionTabId = (typeof SUBMISSION_TABS)[number]["id"];

/** The `?focus=` value that means "the Submission Documents tab" (BRK-S188).
 *  The bond packet notice links here; a constant, because the notice mirror
 *  in `brokerNotifications.ts` spells the other half. */
export const DOCUMENTS_FOCUS = "documents";

/**
 * Which tab holds a `?focus=` deep link's anchor, so the inbox's links keep
 * landing: the GIA reminder (`gia`, BRK-S182) on the Profile, the bond packet
 * notice (`documents`, BRK-S188) on the Documents. `bid-result` is not here:
 * the page redirects it to the bid result screen (BRK-S183).
 */
const FOCUS_TABS: Record<string, SubmissionTabId> = {
  gia: "profile",
  [DOCUMENTS_FOCUS]: "documents",
};

export function tabForFocus(focus: string | null): SubmissionTabId | null {
  return focus !== null && Object.hasOwn(FOCUS_TABS, focus)
    ? FOCUS_TABS[focus]
    : null;
}

// ── The action row ───────────────────────────────────────────────────────────

export type SubmissionRowAction = Extract<
  BondActionKey,
  "message" | "docs" | "edit" | "rider" | "claim" | "withdraw"
>;

/** The fixed row: the wireframe's six, in its order, at every step — the
 *  ones that do not apply are greyed with PMS's reason (lifecycle
 *  reference). */
export const SUBMISSION_ACTION_ROW: readonly {
  key: SubmissionRowAction;
  label: string;
}[] = [
  { key: "message", label: "Message Signal" },
  { key: "docs", label: "Upload Documents" },
  { key: "edit", label: "Edit Bond Request" },
  { key: "rider", label: "Request Rider" },
  { key: "claim", label: "Report A Claim" },
  { key: "withdraw", label: "Withdraw Bond" },
];

export interface SubmissionActionState extends BondAction {
  key: SubmissionRowAction;
  label: string;
}

const NO_REASON_OFF: BondAction = { enabled: false, reason: null };

export function submissionActionRow(
  order: Pick<BondSubmission, "actions">,
): SubmissionActionState[] {
  const published = readBondActions(order.actions);
  return SUBMISSION_ACTION_ROW.map((a) => ({
    ...a,
    ...(published ? published[a.key] : NO_REASON_OFF),
  }));
}

/** How the Bid Result button reads: the accent call to action, the recorded
 *  result (green won, grey lost), or neutral (greyed). */
export type BidResultTone = "accent" | "won" | "lost" | "neutral";

export interface BidResultButtonState extends BondAction {
  label: string;
  tone: BidResultTone;
}

/**
 * The Bid Result button (BRK-S422, BRK-S502): "Bid Result" while none is on
 * file, "Bid Result: Awarded" (green) or "Bid Result: Not Awarded" (grey)
 * once PMS records one — the lifecycle reference's "shows result
 * thereafter". Whether it opens is `actions.bidResult` alone.
 */
export function bidResultButton(
  order: Pick<BondSubmission, "actions" | "bidResult">,
): BidResultButtonState {
  const action = readBondActions(order.actions)?.bidResult ?? NO_REASON_OFF;
  const fact = bidResultFact(order);
  if (fact === "won") {
    return { ...action, label: "Bid Result: Awarded", tone: "won" };
  }
  if (fact === "lost") {
    return { ...action, label: "Bid Result: Not Awarded", tone: "lost" };
  }
  return {
    ...action,
    label: "Bid Result",
    tone: action.enabled ? "accent" : "neutral",
  };
}

// ── The header pill ──────────────────────────────────────────────────────────

/** A pill's colour, as the page maps it onto the shared badge palette. */
export type PillTone = "done" | "active" | "attention" | "danger" | "neutral";

export interface HeaderPill {
  label: string;
  tone: PillTone;
}

const HEADER_PILLS = {
  draft: { label: "Draft", tone: "neutral" },
  submitted: { label: "Submitted", tone: "active" },
  bid_bond_issued: { label: "Bid bond issued", tone: "done" },
  awarded: { label: "Awarded", tone: "done" },
  bonded: { label: "Bonded", tone: "done" },
  maintenance: { label: "Maintenance", tone: "active" },
  closed: { label: "Closed", tone: "neutral" },
  // Outside the bond lifecycle: a discarded draft (BRK-S181) and a filed
  // standalone pre-approval, which reads Complete as its list row does
  // (BRK-S160).
  cancelled: { label: "Cancelled", tone: "neutral" },
  complete: { label: "Complete", tone: "done" },
} as const satisfies Record<BondHeaderStatus, HeaderPill>;

/** A served header word this build has not adopted: neutral, never blank. */
const UNKNOWN_PILL: HeaderPill = { label: "In progress", tone: "neutral" };

/**
 * The header's ONE status pill (the October wireframe, BRK-S502) — PMS's
 * served `headerStatus` (PMS-S837, D17), labelled — plus "Rider submitted" /
 * "Changes submitted" beside it while a change is pending.
 *
 * The portal decides nothing here: the rule that used to compose the pill
 * from the phase, the bonds and the bid result (`submissionHeaderPills`) was
 * deleted in BRK-S511 once PMS served the word. A lost bid and a withdrawal
 * are Closed like any ended job; the withdrawn notice and the tracker say
 * which. A word this build has not adopted reads "In progress".
 */
export function headerPills(
  order: Pick<BondSubmission, "headerStatus" | "changeRequest">,
): HeaderPill[] {
  const status = readHeaderStatus(order.headerStatus);
  const pills: HeaderPill[] = [
    status === null ? UNKNOWN_PILL : HEADER_PILLS[status],
  ];
  const change = changeRequestFact(order);
  if (change === "rider_pending") {
    pills.push({ label: "Rider submitted", tone: "attention" });
  } else if (change === "edit_pending") {
    pills.push({ label: "Changes submitted", tone: "attention" });
  }
  return pills;
}

// ── The button row's chips ───────────────────────────────────────────────────

/**
 * The Program Enrollment chip: the ACCOUNT's standing program, as the account
 * read publishes it. `null` — not yet read, or the read failed — says so
 * rather than claiming the insured holds nothing. The button row's Program
 * Enrollment BUTTON adds the capacity states (`programEnrollmentButton`).
 */
export function programChip(
  preapproval: Pick<AccountPreapproval, "status" | "programType"> | null,
): HeaderPill {
  if (preapproval === null) {
    return { label: "Program unavailable", tone: "neutral" };
  }
  if (preapproval.status !== "approved") {
    return { label: "No program", tone: "attention" };
  }
  const label =
    preapproval.programType === "express"
      ? "Signal Express"
      : preapproval.programType === "advance"
        ? "Signal Advance"
        : "Surety program";
  return { label, tone: "done" };
}

// ── The Program Enrollment button (BRK-S422) ─────────────────────────────────
//
// The lifecycle reference's § 4 table, READ — never computed. WHICH program
// comes from the account read (the order view names no program type); WHETHER
// this bond fits comes from PMS's `capacityCheck` (PMS-S841) alone, and the
// "above the program maximum" override is its `hardOver`, whose figure is the
// served `program.maxSingleLimit` (PMS-S837) — there is no limit constant in
// the SPA. Without a verdict the button names the program and says nothing
// else: a client comparison would be a second answer that could disagree.

/** The button's colour: green tick, yellow upgrade, red, or plain. */
export type ProgramButtonTone = "ok" | "upgrade" | "danger" | "neutral";

/** The glyph beside the label: ✓, ⓘ, or ↗ (it leaves for the account). */
export type ProgramButtonGlyph = "check" | "info" | "external";

export interface ProgramEnrollmentState {
  label: string;
  tone: ProgramButtonTone;
  glyph: ProgramButtonGlyph | null;
  /** What a click does: open the account page to enroll, confirm the jump to
   *  the Signal Advance application, or nothing (status only). */
  action: "enroll" | "advance" | null;
  /** The state, in words; null when there is none to name. Never a score, a
   *  reason or an overage. */
  tooltip: string | null;
  /** The Advance-redirect confirm's sentence: WHICH limit, never a figure. */
  capacity: string | null;
}

const PROGRAM_NAMES: Record<string, string> = {
  express: "Signal Express",
  advance: "Signal Advance",
};

const plainProgram = (label: string): ProgramEnrollmentState => ({
  label,
  tone: "neutral",
  glyph: null,
  action: null,
  tooltip: null,
  capacity: null,
});

/**
 * The Program Enrollment button: No Program (red, enroll) · within (green
 * tick) · Express over (yellow, the Advance-redirect confirm) · Advance over
 * (red, status) · above the program maximum on any program (red, status,
 * overriding the rest) · the name alone without a verdict.
 */
export function programEnrollmentButton(
  preapproval:
    | Pick<AccountPreapproval, "status" | "programType">
    | null
    | "loading",
  order: Pick<BondSubmission, "program" | "capacityCheck"> & {
    lifecycle?: unknown;
  },
): ProgramEnrollmentState {
  if (preapproval === "loading") return plainProgram("Loading…");
  // A failed read says so — never that the insured holds nothing.
  if (preapproval === null) return plainProgram("Program unavailable");
  if (preapproval.status !== "approved") {
    return {
      label: "No Program",
      tone: "danger",
      glyph: "info",
      action: "enroll",
      tooltip:
        "Not enrolled in a surety program. Program enrollment is needed before issuance; click to enroll on the account page.",
      capacity: null,
    };
  }
  const type = preapproval.programType;
  const name =
    type !== null && Object.hasOwn(PROGRAM_NAMES, type)
      ? PROGRAM_NAMES[type]
      : "Surety program";
  const check = readCapacityCheck(order.capacityCheck);
  const phase = readBondLifecycle(order.lifecycle)?.phase ?? null;
  // Completion releases the job's share: a released bond is never "over".
  if (check === null || (phase !== null && RELEASED_PHASES.includes(phase))) {
    return plainProgram(name);
  }
  if (check.withinLimits) {
    return {
      label: name,
      tone: "ok",
      glyph: "check",
      action: null,
      tooltip: `${name}: within the program limits`,
      capacity: null,
    };
  }
  const red = (tooltip: string): ProgramEnrollmentState => ({
    label: name,
    tone: "danger",
    glyph: "info",
    action: null,
    tooltip,
    capacity: null,
  });
  if (check.hardOver) {
    const max = order.program?.maxSingleLimit;
    const figure =
      typeof max === "number" && max > 0
        ? `the ${formatPreapprovalMoney(max)} maximum`
        : "the maximum";
    return red(
      `Over the available limits: above ${figure} single bond limit on any Signal program. Signal reviews before issuing.`,
    );
  }
  if (type === "express") {
    const capacity =
      check.overLimit === null
        ? "This bond is over the program limits."
        : OVER_SENTENCE[check.overLimit];
    return {
      label: name,
      tone: "upgrade",
      glyph: "external",
      action: "advance",
      tooltip: `Not enough Signal Express capacity. ${capacity} Click to apply for Signal Advance.`,
      capacity,
    };
  }
  if (type === "advance") {
    return red("Not enough Advance capacity; Signal reviews before issuing");
  }
  return red("Over the program limits; Signal reviews before issuing");
}

/** The GIA Form button's tooltip while the GIA is not signed — advisory
 *  (D2): readiness is never a gate PMS enforces, so it says what is needed,
 *  never that anything is blocked. */
export const GIA_NEEDED_TIP =
  "This needs to be completed before a Bid Bond can be issued";

/** How the GIA Form button reads: green with a tick (`signed`), red with an
 *  info glyph (`needed`), or `neutral`. */
export type GiaButtonTone = "signed" | "needed" | "neutral";

export interface GiaButtonState {
  label: string;
  tone: GiaButtonTone;
  /** The red state's tooltip; null otherwise. */
  tooltip: string | null;
  /** The button's accessible name, which carries its state. */
  name: string;
}

/**
 * The GIA Form button (BRK-S423), off the order's served `gia.status` alone.
 *
 * Signed — or a GIA already on file that covers the order (`not_required`)
 * — is green. Every status that is not signed — not sent, sent, declined,
 * voided — is red with the advisory tooltip. A status this build does not
 * know, and an order with no `gia` block (PMS publishes none before approval),
 * are neutral: never a guess at red or green.
 */
export function giaButton(order: Pick<BondSubmission, "gia">): GiaButtonState {
  const neutral = (name: string): GiaButtonState => ({
    label: "GIA Form",
    tone: "neutral",
    tooltip: null,
    name,
  });
  if (!order.gia) return neutral("GIA Form: not requested yet");
  const status = readGiaStatus(order.gia.status);
  switch (status) {
    case null:
      return neutral("GIA Form: status unavailable");
    case "signed":
      return {
        label: "GIA Form",
        tone: "signed",
        tooltip: null,
        name: "GIA Form: signed",
      };
    case "not_required":
      // A GIA on file covers this order: done, so green (the wireframe's
      // GIA Form is red or green, BRK-S502).
      return {
        label: "GIA Form",
        tone: "signed",
        tooltip: null,
        name: "GIA Form: on file",
      };
    default:
      return {
        label: "GIA Form",
        tone: "needed",
        tooltip: GIA_NEEDED_TIP,
        name: `GIA Form: not signed. ${GIA_NEEDED_TIP}`,
      };
  }
}

// ── The way back ─────────────────────────────────────────────────────────────

/**
 * The one way back from a filed submission (BRK-S318's one-back rule): to the
 * ACCOUNT it belongs to, named for it (BRK-S399, the wireframe's "Back to
 * Test Construction Company").
 */
export function backToAccountLabel(name: string | null | undefined): string {
  const trimmed = name?.trim();
  return trimmed ? `Back to ${trimmed}` : "Back to the account";
}
